// Variant-only copy overrides for the startviralgpt.com landing page.
//
// These override the shared language dictionary (lang/*.public.js) for ad
// compliance + believability WITHOUT touching the main viralgpt.com site.
// useVariantCopy() deep-merges these onto the shared L: only the keys present
// here change; everything else falls back to the shared copy.
//
// Merge rules (see useVariantCopy.js):
//   - objects merge recursively
//   - arrays merge element-by-element; use `null` to keep the shared value at
//     that index (e.g. card1Items keeps items 0-1, overrides 2-3)
//   - keys not present in the shared copy are added (e.g. heading)
//
// Hero copy IS overridden here. The shared dictionary still describes the
// original monthly subscription — "real followers every month", "Real, Organic",
// "personalized AI targeting" — none of which describes what this funnel sells:
// a one-time delivery of a fixed follower count, fulfilled through a panel
// service that does not refill drops. Ad traffic lands on these words, so they
// state what is actually delivered and nothing more. The main site keeps the
// shared copy, since its legacy plans do deliver monthly.

export const overrides = {
  en: {
    header: {
      heroSub:
        "Choose how many followers you want and we deliver them to your Instagram.",
      titlePre: "Instagram",
      subtitle:
        "Pick a package, enter your username, and we start delivering. No password needed.",
    },
    faq: {
      items: [
        {
          content:
            "We deliver followers from real accounts, not empty bot profiles. Some accounts do unfollow over time, which is normal on Instagram — these packages are a one-time delivery and do not include automatic replacement of drops.",
        },
        null,
        null,
        {
          content:
            "We're a growth service for creators, brands and businesses. You pick a follower package, we deliver it to your account, and your dashboard lets you buy more whenever you want — plus AI tools for captions, trends and analytics.",
        },
      ],
    },
    // No `pricing` override. The variant's pricing block renders
    // config/packages.js through the shared `packages` copy, which already
    // states the one-time price and the auto-refill that comes with it — so
    // there is nothing to override, and overriding it is how this page would
    // end up disclosing less than the main site.
    //
    // What used to sit here was a full `pricing.plans` clone: "40,000+
    // followers", "Estimated monthly growth, delivered gradually across your
    // billing cycle". Nothing read it, and it was the exact subscription
    // language this file exists to keep off the page.
    testimonials: {
      sub: "Real creators sharing their experience.",
      reviews: {
        daniel_slovak:
          "ViralGPT helped me reach more people in my niche and improve my follower growth over time.",
        smoke:
          "Over a few months my engagement improved and the new followers were genuinely interested in my content.",
        vikky:
          "ViralGPT helped me reach the right audience and grow more steadily than before.",
      },
      smokeTitle: "TopTree CEO · 359k followers",
      ctaButton: "Get Started",
      ctaSub: "No password required · No bots · Cancel auto-refill anytime",
    },
    footer: {
      platforms: [
        { title: "Grow on Instagram", sub: "More Followers" },
        { title: "Increase TikTok visibility", sub: "Audience Growth" },
        { title: "Master YouTube", sub: "More Subscribers" },
        { title: "Grow on X", sub: "More Followers" },
      ],
      riskFree: "Try ViralGPT with a 7-day money-back guarantee.",
      description:
        "We grow social accounts with real, targeted followers — plus AI tools for captions, trends, and analytics.",
      disclaimer:
        "ViralGPT is not affiliated with Instagram, Meta, TikTok, YouTube, X, or any other social media platform. Results vary and are not guaranteed. Growth depends on account quality, content, targeting, niche, and user activity.",
    },
  },

  es: {
    header: {
      heroSub:
        "Elige cuántos seguidores quieres y los entregamos en tu Instagram.",
      titlePre: "Seguidores de",
      subtitle:
        "Elige un paquete, ingresa tu usuario y empezamos a entregar. Sin contraseña.",
    },
    faq: {
      items: [
        {
          content:
            "Entregamos seguidores de cuentas reales, no perfiles bot vacíos. Algunas cuentas dejan de seguir con el tiempo, algo normal en Instagram — estos paquetes son una entrega única y no incluyen reposición automática de las bajas.",
        },
        null,
        null,
        {
          content:
            "Somos un servicio de crecimiento para creadores, marcas y negocios. Eliges un paquete de seguidores, lo entregamos en tu cuenta y desde tu panel puedes comprar más cuando quieras — además de herramientas de IA para captions, tendencias y analíticas.",
        },
      ],
    },
    // Sin override de `pricing` — ver la nota en la sección `en`.
    testimonials: {
      sub: "Creadores reales compartiendo su experiencia.",
      reviews: {
        daniel_slovak:
          "ViralGPT me ayudó a llegar a más personas en mi nicho y a mejorar mi crecimiento de seguidores con el tiempo.",
        smoke:
          "En unos meses mi interacción mejoró y los nuevos seguidores estaban genuinamente interesados en mi contenido.",
        vikky:
          "ViralGPT me ayudó a llegar a la audiencia correcta y a crecer de forma más constante que antes.",
      },
      smokeTitle: "CEO de TopTree · 359k seguidores",
      ctaButton: "Comenzar",
      ctaSub: "No se requiere contraseña · Sin bots · Cancela la reposición cuando quieras",
    },
    footer: {
      platforms: [
        { title: "Crece en Instagram", sub: "Más seguidores" },
        { title: "Aumenta tu visibilidad en TikTok", sub: "Crecimiento de audiencia" },
        { title: "Domina YouTube", sub: "Más suscriptores" },
        { title: "Crece en X", sub: "Más seguidores" },
      ],
      riskFree: "Prueba ViralGPT con una garantía de reembolso de 7 días.",
      description:
        "Hacemos crecer cuentas sociales con seguidores reales y segmentados — más herramientas de IA para captions, tendencias y analíticas.",
      disclaimer:
        "ViralGPT no está afiliado a Instagram, Meta, TikTok, YouTube, X ni ninguna otra plataforma de redes sociales. Los resultados varían y no están garantizados. El crecimiento depende de la calidad de la cuenta, el contenido, la segmentación, el nicho y la actividad del usuario.",
    },
  },
};
